<template>
  <div class="team" v-if="!page404">
    <XNav :content="[{ t: 'common.teams' }, { name: teamData.Name }]" />
    <h1 v-if="teamData.Name" class="sr-only">{{ teamData.Name }}</h1>
    <TeamBanner
      v-if="leagueInfo"
      :team-name="teamData.Name"
      :team-id="$route.params.id"
      :subtitle="leagueInfo.SeasonName + ' - ' + leagueInfo.FullName"
    />
    <XTabs :x-tabs="tabs" class="tabs">
      <template #tab[schedule]>
        <MatchTable :content="matches" />
      </template>
      <template #tab[players]>
        <PlayersTable :content="playersList" />
      </template>
      <template #tab[standings]>
        <LeagueTable :content="leagueTable" />
      </template>
    </XTabs>
  </div>
  <Page404 v-else />
</template>

<script>
import axios from "axios";
import LeagueTable from "@/components/tables/LeagueTable.vue";
import MatchTable from "@/components/tables/MatchTable.vue";
import PlayersTable from "@/components/tables/PlayersTable.vue";
import XTabs from "@/components/XTabs.vue";
import TeamBanner from "@/components/TeamBanner.vue";
import XNav from "@/components/XNav.vue";
import Page404 from "@/views/404.vue";
export default {
  components: {
    LeagueTable,
    MatchTable,
    PlayersTable,
    XTabs,
    TeamBanner,
    XNav,
    Page404,
  },
  metaInfo() {
    return {
      title: this.$t("seo_metadata.team.title", { team: this.teamData.Name }),
      meta: [
        { vmid: "description", name: "description", content: this.$t("seo_metadata.team.description", { team: this.teamData.Name }) },
        { vmid: "keywords", name: "keywords", content: this.$t("seo_metadata.keywords") },
        { vmid: "og:title", property: "og:title", content: this.$t("seo_metadata.team.title", { team: this.teamData.Name }) },
        { vmid: "og:description", property: "og:description", content: this.$t("seo_metadata.team.description", { team: this.teamData.Name }) },
        { vmid: "og:url", property: "og:url", content: `https://kingsleague.ca/team/${this.$route.params.id}/${this.$route.params.leagueID}` },
        { vmid: "og:type", property: "og:type", content: "website"},
        { vmid: "twitter:title", name: "twitter:title", content: this.$t("seo_metadata.team.title", { team: this.teamData.Name }) },
        { vmid: "twitter:description", name: "twitter:description", content: this.$t("seo_metadata.team.description", { team: this.teamData.Name }) }
      ],
      link: [{ rel: "canonical", href: "https://kingsleague.ca" + this.$route.path }],
      script: this.teamData.Name ? [{
        type: "application/ld+json",
        json: {
          "@context": "https://schema.org",
          "@type": "SportsTeam",
          "name": this.teamData.Name,
          "sport": "Soccer",
          "url": "https://kingsleague.ca" + this.$route.path,
          "memberOf": {
            "@type": "SportsOrganization",
            "name": "The Kings League",
            "url": "https://kingsleague.ca"
          },
          ...(this.leagueInfo ? {
            "description": this.teamData.Name + " plays in the " + this.leagueInfo.FullName + " at The Kings League, Montreal's adult soccer league."
          } : {})
        }
      }, {
        type: "application/ld+json",
        json: {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://kingsleague.ca/" },
            { "@type": "ListItem", "position": 2, "name": "Teams", "item": "https://kingsleague.ca/" },
            { "@type": "ListItem", "position": 3, "name": this.teamData.Name }
          ]
        }
      }] : []
    };
  },
  data() {
    return {
      leagueTable: [],
      matches: [],
      playersList: [],
      teamData: {
        Name: null,
      },
      leagueID: null,
      leagueInfo: null,
      tabs: [
        { name: "schedule", display: "team.schedule" },
        { name: "players", display: "team.players" },
        { name: "standings", display: "team.standings" },
      ],
      seasonQuery: "",
      page404: false,
    };
  },
  mounted() {
    this.seasonQuery = this.$route.params.seasonID ? "&seasonID=" + this.$route.params.seasonID : "";

    Promise.all([
      this.getTeams(),
      this.getPlayersInTeam(),
      this.getLeagueInfo().then(() =>
        Promise.all([this.getTeamMatchInfo(), this.getLeagueTable()])
      ),
    ])
      .then(() => this.$store.dispatch("pageLoad", false))
      .catch(() => {
        this.page404 = true;
        this.$store.dispatch("pageLoad", false);
      });
  },
  methods: {
    goPlayerPage(item) {
      this.$router.push({ path: `/players/${item.Player_id}` });
    },
    async getTeams() {
      const res = await axios
        .get(
          `${this.$apiUrl}/teams/${this.$route.params.id}?${this.seasonQuery}`
        );
      this.teamData = res.data;
    },
    async getPlayersInTeam() {
      const res = await axios
        .get(
          `${this.$apiUrl}/teams/${this.$route.params.id}/players?${this.seasonQuery}&leagueID=${this.$route.params.leagueID}`
        );
      this.playersList = res.data;
    },
    async getLeagueInfo() {
      const res = await axios
        .get(`${this.$apiUrl}/leagues/${this.$route.params.leagueID}?${this.seasonQuery}`);
      this.leagueInfo = res.data;
      this.leagueID = res.data._id;
    },
    async getTeamMatchInfo() {
      const res = await axios
        .get(
          `${this.$apiUrl}/teams/${this.$route.params.id}/matches?leagueID=${this.$route.params.leagueID}${this.seasonQuery}`
        );
      this.matches = res.data;
    },
    async getLeagueTable() {
      const res = await axios
        .get(`${this.$apiUrl}/leagueTable/${this.$route.params.leagueID}?${this.seasonQuery}`);
      this.leagueTable = res.data;
    },
  },
};
</script>
<style lang="scss" scoped>
@import "@/theme.scss";
.tabs {
  margin: 15px 0;
}
.box {
  margin: 0;
}
.test {
  height: 200px;
}
</style>
